import { useState } from 'react';
import { useIntegrations } from '@/hooks/useIntegrations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Database, Loader2, Trash2, CheckCircle2, ExternalLink } from 'lucide-react';
import type { SupabaseIntegration } from '@/contexts/IntegrationsContext';
import {
  createSupabaseIntegration,
  validateSupabaseConnection,
  extractProjectRef,
} from '@/lib/integrations/supabase';

export default function DatabaseSettings() {
  const { settings, removeIntegration, addIntegration, updateIntegration } = useIntegrations();
  const [integrationToDelete, setIntegrationToDelete] = useState<SupabaseIntegration | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Supabase state
  const [isSupabaseDialogOpen, setIsSupabaseDialogOpen] = useState(false);
  const [supabaseForm, setSupabaseForm] = useState({
    name: '',
    projectUrl: '',
    anonKey: '',
    serviceRoleKey: '',
  });
  const [isValidatingSupabase, setIsValidatingSupabase] = useState(false);
  const [supabaseError, setSupabaseError] = useState<string | null>(null);

  const supabaseIntegrations = settings.integrations.filter(
    (i): i is SupabaseIntegration => i.type === 'supabase'
  );

  const handleAddSupabase = async () => {
    setSupabaseError(null);
    setIsValidatingSupabase(true);

    try {
      // Validate URL format
      let projectUrl = supabaseForm.projectUrl.trim();
      if (!projectUrl.startsWith('http://') && !projectUrl.startsWith('https://')) {
        projectUrl = `https://${projectUrl}`;
      }

      // Extract project ref to validate URL format
      const projectRef = extractProjectRef(projectUrl);

      // Validate connection
      const isValid = await validateSupabaseConnection(projectUrl, supabaseForm.anonKey.trim());
      if (!isValid) {
        throw new Error('Failed to connect to Supabase. Please check your credentials.');
      }

      // Create integration
      const integration = await createSupabaseIntegration(
        crypto.randomUUID(),
        supabaseForm.name.trim() || `Supabase (${projectRef})`,
        projectUrl,
        supabaseForm.anonKey.trim(),
        supabaseForm.serviceRoleKey.trim() || undefined
      );

      addIntegration(integration);

      // Reset form
      setSupabaseForm({
        name: '',
        projectUrl: '',
        anonKey: '',
        serviceRoleKey: '',
      });
      setIsSupabaseDialogOpen(false);
    } catch (error) {
      console.error('Failed to add Supabase integration:', error);
      setSupabaseError(error instanceof Error ? error.message : 'Failed to add integration');
    } finally {
      setIsValidatingSupabase(false);
    }
  };

  const handleDeleteIntegration = async () => {
    if (!integrationToDelete) return;

    setIsDeleting(true);
    try {
      removeIntegration(integrationToDelete.id);
      setIntegrationToDelete(null);
    } catch (error) {
      console.error('Failed to delete integration:', error);
      alert('Failed to delete integration. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleIntegration = (integration: SupabaseIntegration) => {
    updateIntegration(integration.id, { enabled: !integration.enabled });
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Database</h1>
        <p className="text-muted-foreground mt-2">
          Connect database providers to add persistent storage to your projects
        </p>
      </div>

      <Separator />

      {/* Supabase Section */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Database className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Supabase</CardTitle>
                <CardDescription>PostgreSQL database with auto-generated API</CardDescription>
              </div>
            </div>
            <Button onClick={() => setIsSupabaseDialogOpen(true)}>
              Connect Project
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {supabaseIntegrations.length === 0 ? (
            <div className="space-y-4">
              <Alert>
                <Database className="h-4 w-4" />
                <AlertDescription>
                  Connect a Supabase project to add database functionality to your apps. Free tier includes 500MB database and 50K monthly active users.
                </AlertDescription>
              </Alert>

              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <h3 className="font-medium text-sm">What you'll need:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>A Supabase account (free tier available)</li>
                  <li>Your project URL (e.g., https://xxxxx.supabase.co)</li>
                  <li>Your project's anon/public key</li>
                  <li>Optionally: service_role key for admin operations</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-2">
                  Find these in your{' '}
                  <a
                    href="https://supabase.com/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    Supabase Dashboard
                    <ExternalLink className="h-3 w-3" />
                  </a>{' '}
                  under Project Settings → API
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium">Connected Projects</p>
              {supabaseIntegrations.map((integration) => (
                <div
                  key={integration.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{integration.name}</p>
                      {integration.enabled && (
                        <Badge variant="outline" className="gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Active
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <span>Project: {integration.projectRef}</span>
                      <span className="font-mono text-xs">{integration.projectUrl}</span>
                    </div>
                    {integration.serviceRoleKey && (
                      <p className="text-xs text-muted-foreground">
                        Service role key configured
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`toggle-${integration.id}`} className="text-sm cursor-pointer">
                        {integration.enabled ? 'Enabled' : 'Disabled'}
                      </Label>
                      <Switch
                        id={`toggle-${integration.id}`}
                        checked={integration.enabled}
                        onCheckedChange={() => handleToggleIntegration(integration)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIntegrationToDelete(integration)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => setIsSupabaseDialogOpen(true)}
              >
                Connect Another Project
              </Button>
            </div>
          )}

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Once enabled, the AI can help you add database tables, queries, and hooks to your projects using Supabase.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Supabase Connection Dialog */}
      <Dialog open={isSupabaseDialogOpen} onOpenChange={setIsSupabaseDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Connect Supabase Project</DialogTitle>
            <DialogDescription>
              Enter your Supabase project credentials. You can find these in your Supabase Dashboard
              under Project Settings → API.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="supabase-name">
                Integration Name <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="supabase-name"
                placeholder="My Supabase Project"
                value={supabaseForm.name}
                onChange={(e) => setSupabaseForm({ ...supabaseForm, name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Give this integration a descriptive name
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="supabase-url">
                Project URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="supabase-url"
                type="url"
                placeholder="https://xxxxx.supabase.co"
                value={supabaseForm.projectUrl}
                onChange={(e) => setSupabaseForm({ ...supabaseForm, projectUrl: e.target.value })}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Your Supabase project URL (found in Project Settings → API)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="supabase-anon-key">
                Anon Key <span className="text-destructive">*</span>
              </Label>
              <Input
                id="supabase-anon-key"
                type="password"
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                value={supabaseForm.anonKey}
                onChange={(e) => setSupabaseForm({ ...supabaseForm, anonKey: e.target.value })}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Your project's public anon key (safe to use in client-side code)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="supabase-service-key">
                Service Role Key <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="supabase-service-key"
                type="password"
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                value={supabaseForm.serviceRoleKey}
                onChange={(e) => setSupabaseForm({ ...supabaseForm, serviceRoleKey: e.target.value })}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                For admin operations (bypasses Row Level Security). Keep this secret!
              </p>
            </div>

            {supabaseError && (
              <Alert variant="destructive">
                <AlertDescription>{supabaseError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsSupabaseDialogOpen(false);
                setSupabaseError(null);
              }}
              disabled={isValidatingSupabase}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSupabase}
              disabled={
                !supabaseForm.projectUrl.trim() ||
                !supabaseForm.anonKey.trim() ||
                isValidatingSupabase
              }
            >
              {isValidatingSupabase ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validating...
                </>
              ) : (
                'Connect Project'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!integrationToDelete}
        onOpenChange={(open) => !open && setIntegrationToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Integration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect "{integrationToDelete?.name}"? This will remove
              the integration from all your projects. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteIntegration}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
